import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import AdminRete from "./AdminRete";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

const emptyWebinar = { title: "", description: "", image_url: "", date: "", time: "", duration_minutes: 60, price_eur: 0, seats_total: 30, seats_available: 30, status: "open", booking_url: "" };
const emptyPost = { title: "", excerpt: "", content: "", cover_url: "", categories: [], tags: [], author: "Dania (Dan) Piras", read_minutes: 5, seo_title: "", seo_description: "", published: true };
const emptyBook = { title: "", subtitle: "", description: "", cover_url: "", excerpt: "", reviews: [], buy_links: [], year: new Date().getFullYear(), published: true };
const emptyEvent = { title: "", description: "", cover_url: "", images: [], place: "", date: "", external_url: "", published: true };
const emptyMedia = { title: "", description: "", kind: "article", url: "", cover_url: "", source: "", date: "", published: true };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-viola/70">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function TextInput(props) {
  return <input {...props} className={`w-full rounded-lg border border-viola/20 bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-fucsia ${props.className || ""}`} />;
}
function TextArea(props) {
  return <textarea {...props} className={`w-full rounded-lg border border-viola/20 bg-cream px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-fucsia ${props.className || ""}`} />;
}

function CrudSection({ endpoint, empty, columns, renderForm, testKey }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => http.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (item) => { setForm({ ...empty, ...item }); setEditing(item.id); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    await http.delete(`${endpoint}/${id}`);
    toast.success("Eliminato");
    load();
  };
  const save = async () => {
    try {
      if (editing === "new") {
        await http.post(endpoint, form);
        toast.success("Creato");
      } else {
        await http.put(`${endpoint}/${editing}`, form);
        toast.success("Aggiornato");
      }
      cancel(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-viola/60">{items.length} elementi</p>
        <button onClick={startNew} className="btn-primary" data-testid={`${testKey}-new`}><Plus size={14} /> Nuovo</button>
      </div>

      {editing && (
        <div className="mb-8 rounded-2xl border border-viola/10 bg-lilla/40 p-6 space-y-4" data-testid={`${testKey}-form`}>
          <p className="font-display text-2xl">{editing === "new" ? "Nuovo elemento" : "Modifica"}</p>
          {renderForm(form, setForm)}
          <div className="flex gap-3">
            <button onClick={save} className="btn-primary" data-testid={`${testKey}-save`}>Salva</button>
            <button onClick={cancel} className="btn-ghost" data-testid={`${testKey}-cancel`}>Annulla</button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-viola/10 overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-viola text-cream">
            <tr>
              {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-medium">{c.label}</th>)}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-viola/10" data-testid={`${testKey}-row-${it.id}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3">{c.render ? c.render(it) : String(it[c.key] ?? "")}</td>)}
                <td className="px-4 py-3 flex gap-2 justify-end">
                  <button onClick={() => startEdit(it)} className="text-viola/70 hover:text-fucsia" aria-label="Edit" data-testid={`${testKey}-edit-${it.id}`}><Edit3 size={16} /></button>
                  <button onClick={() => remove(it.id)} className="text-viola/70 hover:text-fucsia" aria-label="Delete" data-testid={`${testKey}-delete-${it.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-viola/50">Nessun elemento.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth() || {};
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (!loading && user && !user.is_admin) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      http.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      http.get("/newsletter/subscribers").then((r) => setSubs(r.data)).catch(() => {});
      http.get("/contact/messages").then((r) => setMessages(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="container-editorial py-24">Caricamento…</div>;
  if (!user?.is_admin) return null;

  return (
    <div className="container-editorial py-16" data-testid="page-admin">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <span className="pill">Amministrazione</span>
          <h1 className="mt-3 font-display text-4xl">Ciao, {user.name}</h1>
        </div>
        <button onClick={logout} className="btn-ghost" data-testid="admin-logout"><LogOut size={14} /> Esci</button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[["Webinar", stats.webinars], ["Articoli", stats.posts], ["Libri", stats.books], ["Eventi", stats.events], ["Media", stats.media], ["Newsletter", stats.newsletter], ["Messaggi", stats.messages]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-lilla/60 p-5">
              <p className="text-xs uppercase tracking-widest text-viola/60 font-semibold">{l}</p>
              <p className="mt-2 font-display text-3xl">{v ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        <Tabs defaultValue="webinars">
          <TabsList data-testid="admin-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="webinars" data-testid="tab-webinars">Webinar</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts">Blog</TabsTrigger>
            <TabsTrigger value="books" data-testid="tab-books">Libri</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Eventi</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="rete" data-testid="tab-rete">Rete GSRD</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messaggi</TabsTrigger>
          </TabsList>

          <TabsContent value="webinars" className="mt-6">
            <CrudSection
              endpoint="/webinars"
              empty={emptyWebinar}
              testKey="webinar"
              columns={[
                { key: "title", label: "Titolo" },
                { key: "date", label: "Data" },
                { key: "status", label: "Stato" },
                { key: "price_eur", label: "Prezzo", render: (i) => (i.price_eur ? `${i.price_eur} €` : "Gratuito") },
              ]}
              renderForm={(form, setForm) => (
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Titolo"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="w-title" /></Field>
                  <Field label="Immagine (URL)"><TextInput value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></Field>
                  <Field label="Descrizione"><TextArea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="w-desc" /></Field>
                  <div />
                  <Field label="Data (YYYY-MM-DD)"><TextInput value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="w-date" /></Field>
                  <Field label="Ora (HH:MM)"><TextInput value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></Field>
                  <Field label="Durata (min)"><TextInput type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} /></Field>
                  <Field label="Prezzo €"><TextInput type="number" step="0.01" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: Number(e.target.value) })} /></Field>
                  <Field label="Posti totali"><TextInput type="number" value={form.seats_total} onChange={(e) => setForm({ ...form, seats_total: Number(e.target.value) })} /></Field>
                  <Field label="Posti disponibili"><TextInput type="number" value={form.seats_available} onChange={(e) => setForm({ ...form, seats_available: Number(e.target.value) })} /></Field>
                  <Field label="Stato">
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-lg border border-viola/20 bg-cream px-4 py-2.5">
                      <option value="open">Aperto</option>
                      <option value="full">Completo</option>
                      <option value="closed">Concluso</option>
                    </select>
                  </Field>
                  <Field label="URL prenotazione"><TextInput value={form.booking_url} onChange={(e) => setForm({ ...form, booking_url: e.target.value })} /></Field>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="posts" className="mt-6">
            <CrudSection
              endpoint="/posts"
              empty={emptyPost}
              testKey="post"
              columns={[
                { key: "title", label: "Titolo" },
                { key: "author", label: "Autore" },
                { key: "published", label: "Pubblicato", render: (i) => i.published ? "Sì" : "No" },
              ]}
              renderForm={(form, setForm) => (
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Titolo"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="p-title" /></Field>
                  <Field label="Cover URL"><TextInput value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} /></Field>
                  <Field label="Estratto"><TextArea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></Field>
                  <Field label="Autore"><TextInput value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></Field>
                  <Field label="Categorie (separate da virgola)"><TextInput value={(form.categories || []).join(", ")} onChange={(e) => setForm({ ...form, categories: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /></Field>
                  <Field label="Tag (separati da virgola)"><TextInput value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /></Field>
                  <Field label="Minuti di lettura"><TextInput type="number" value={form.read_minutes} onChange={(e) => setForm({ ...form, read_minutes: Number(e.target.value) })} /></Field>
                  <Field label="SEO title"><TextInput value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></Field>
                  <div className="md:col-span-2">
                    <Field label="Contenuto (rich text)">
                      <div className="bg-white rounded-lg border border-viola/20">
                        <ReactQuill
                          theme="snow"
                          value={form.content}
                          onChange={(v) => setForm({ ...form, content: v })}
                          modules={QUILL_MODULES}
                          data-testid="p-content"
                        />
                      </div>
                    </Field>
                  </div>
                  <label className="flex items-center gap-2 md:col-span-2">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                    <span>Pubblicato</span>
                  </label>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="books" className="mt-6">
            <CrudSection
              endpoint="/books"
              empty={emptyBook}
              testKey="book"
              columns={[
                { key: "title", label: "Titolo" },
                { key: "year", label: "Anno" },
                { key: "published", label: "Pubblicato", render: (i) => i.published ? "Sì" : "No" },
              ]}
              renderForm={(form, setForm) => (
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Titolo"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="b-title" /></Field>
                  <Field label="Sottotitolo"><TextInput value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Field>
                  <Field label="Cover URL"><TextInput value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} /></Field>
                  <Field label="Anno"><TextInput type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} /></Field>
                  <div className="md:col-span-2">
                    <Field label="Descrizione"><TextArea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Estratto"><TextArea rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Recensioni (JSON: [{author, text, source}])">
                      <TextArea rows={3} value={JSON.stringify(form.reviews || [])} onChange={(e) => {
                        try {
                          setForm({ ...form, reviews: JSON.parse(e.target.value) });
                        } catch (err) {
                          // Ignore JSON parse errors while user is still typing (partial input)
                        }
                      }} />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Link acquisto (JSON: [{label, url}])">
                      <TextArea rows={2} value={JSON.stringify(form.buy_links || [])} onChange={(e) => {
                        try {
                          setForm({ ...form, buy_links: JSON.parse(e.target.value) });
                        } catch (err) {
                          // Ignore JSON parse errors while user is still typing (partial input)
                        }
                      }} />
                    </Field>
                  </div>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="events" className="mt-6">
            <CrudSection
              endpoint="/events"
              empty={emptyEvent}
              testKey="event"
              columns={[
                { key: "title", label: "Titolo" },
                { key: "place", label: "Luogo" },
                { key: "date", label: "Data" },
                { key: "published", label: "Pubblicato", render: (i) => i.published ? "Sì" : "No" },
              ]}
              renderForm={(form, setForm) => (
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Titolo"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="e-title" /></Field>
                  <Field label="Cover URL"><TextInput value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} /></Field>
                  <Field label="Luogo"><TextInput value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} /></Field>
                  <Field label="Data (es. '15 giugno 2026' o '2026-06-15')"><TextInput value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
                  <div className="md:col-span-2">
                    <Field label="Descrizione"><TextArea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                  </div>
                  <Field label="URL esterno (facoltativo)"><TextInput value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} /></Field>
                  <Field label="Galleria (URL separati da virgola)"><TextInput value={(form.images || []).join(", ")} onChange={(e) => setForm({ ...form, images: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /></Field>
                  <label className="flex items-center gap-2 md:col-span-2">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                    <span>Pubblicato</span>
                  </label>
                </div>
              )}
            />
          </TabsContent>

          <TabsContent value="media" className="mt-6">
            <CrudSection
              endpoint="/media"
              empty={emptyMedia}
              testKey="media"
              columns={[
                { key: "title", label: "Titolo" },
                { key: "kind", label: "Tipo" },
                { key: "source", label: "Fonte" },
                { key: "date", label: "Data" },
              ]}
              renderForm={(form, setForm) => (
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Titolo"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="m-title" /></Field>
                  <Field label="Tipo">
                    <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full rounded-lg border border-viola/20 bg-cream px-4 py-2.5">
                      <option value="article">Articolo</option>
                      <option value="podcast">Podcast</option>
                      <option value="video">Video</option>
                      <option value="interview">Intervista</option>
                      <option value="conference">Conferenza</option>
                    </select>
                  </Field>
                  <Field label="URL"><TextInput value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></Field>
                  <Field label="Cover URL"><TextInput value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} /></Field>
                  <Field label="Fonte / Testata"><TextInput value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></Field>
                  <Field label="Data (es. 'giugno 2025')"><TextInput value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
                  <div className="md:col-span-2">
                    <Field label="Descrizione"><TextArea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                  </div>
                  <label className="flex items-center gap-2 md:col-span-2">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                    <span>Pubblicato</span>
                  </label>
                </div>
              )}
            />
          </TabsContent>


          <TabsContent value="newsletter" className="mt-6">
            <div className="rounded-xl border border-viola/10 overflow-hidden bg-white" data-testid="nl-list">
              <table className="w-full text-sm">
                <thead className="bg-viola text-cream"><tr><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">Data</th></tr></thead>
                <tbody>
                  {subs.map((s) => <tr key={s.id} className="border-t border-viola/10"><td className="px-4 py-3">{s.email}</td><td className="px-4 py-3">{s.name || "—"}</td><td className="px-4 py-3">{new Date(s.created_at).toLocaleDateString("it-IT")}</td></tr>)}
                  {subs.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-viola/50">Nessuna iscrizione.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <div className="space-y-3" data-testid="msg-list">
              {messages.length === 0 && <p className="text-viola/50">Nessun messaggio.</p>}
              {messages.map((m) => (
                <div key={m.id} className="rounded-xl border border-viola/10 bg-white p-5">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="font-semibold">{m.name} <span className="text-viola/50 font-normal">· {m.email}</span></p>
                      <p className="text-xs text-viola/60 mt-1">{new Date(m.created_at).toLocaleString("it-IT")} · {m.reason}</p>
                    </div>
                    <span className="pill">{m.reason}</span>
                  </div>
                  {m.subject && <p className="mt-3 font-medium">{m.subject}</p>}
                  <p className="mt-2 text-viola/80 whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="rete" className="mt-6">
            <AdminRete />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
